import React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Table, Button, Dropdown, message, Select, DatePicker, Tabs, Input } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import ConfirmModal from '../../../components/confirm-modal';
import PaginationComponent from '../../../components/pagination';
import moreIcon from 'assets/images/icon_btn_More.svg';
import deleteBatch from 'assets/images/icon_btn_delete_text.svg';
import approveIcon from 'assets/images/approve_action.svg';
import deleteAction from 'assets/images/icon_btn_delete_table.svg';
import downloadIcon from 'assets/images/icon_btn_Download.svg';
import addIcon from 'assets/images/addIcon.png';
import {
	documentTypeOptions,
	approvalStatusOptions,
	providedByOptions,
	documentTypeMapping,
} from '../../../constants/knowledge-base';
import { useAutoSize } from 'hooks';
import { useSelector } from 'react-redux';
import * as Permissions from 'constants/permissions';
import * as api from 'api';
import UploadModal from './components/upload-modal';
import {
	formatDateString,
	displayText,
	displayTextForFileExt,
	displayTextForArray,
	displayTextForFileSize,
	downloadFile,
} from 'utils';
import Documents from './components/documents/documents';
import '../index.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

const TextBase = () => {
	const initialFilter = {
		type: undefined,
		sbu: undefined,
		status: undefined,
		dateRange: undefined,
		filename: undefined,
	};
	const [ref, tableHeight] = useAutoSize();

	const { permissions } = useSelector((state) => state.user);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});
	const [dataSource, setDataSource] = useState([]);
	const [filter, setFilter] = useState(initialFilter);

	const [selectedRowKeys, setSelectedRowKeys] = useState([]);

	const [applicationOptions, setApplicationOptions] = useState([]);

	const [uploadModalVisible, setUploadModalVisible] = useState(false);
	const [uploadDocumentVisible, setUploadDocumentVisible] = useState(false);
	const [confirmModalVisible, setConfirmModalVisible] = useState(false);
	const confirmType = useRef('delete'); // delete | batch-delete | approve

	const [selectedItem, setSelectedItem] = useState(null);

	const selectedAppId = useRef(null);
	const [activeKey, setActiveKey] = useState('knowledgeBase');
	const documentRef = useRef();

	const uploadPermission = useMemo(() => {
		return activeKey === 'knowledgeBase'
			? permissions.includes(Permissions.KnowledgeBase_IMPORT)
			: permissions.includes(Permissions.Document_IMPORT);
	}, [activeKey, permissions]);

	const tabsItems = useMemo(() => {
		const tabs = [];
		if (permissions.includes(Permissions.KnowledgeBase_LIST)) {
			tabs.push({ key: 'knowledgeBase', label: 'Knowledge base', children: null });
		}
		if (permissions.includes(Permissions.Document_LIST)) {
			tabs.push({ key: 'document', label: 'Technical documents', children: null });
		}
		return tabs;
	}, [permissions]);

	useEffect(() => {
		fetchApplications();
	}, []);
	// MARK: - api calls
	async function fetchApplications() {
		try {
			api.getApplications().then((apps) => {
				const options = apps.map((item) => ({
					key: item.id,
					label: item.applicationName,
				}));
				setApplicationOptions(options);
				if (!selectedAppId.current && options.length > 0) {
					const appId = apps[0].id;
					selectedAppId.current = appId;
					getFiles(appId, {
						page: 1,
						size: pagination.pageSize,
					});
				}
			});
		} catch (error) {
			console.error('e', error);
		}
	}

	async function getFiles(appId, params) {
		if (!appId) {
			return;
		}
		try {
			const response = await api.getFiles(appId, params);
			setDataSource(response.files);
			setPagination((prev) => ({ ...prev, total: response.total || 0 }));
		} catch (error) {
			console.error('获取知识库列表失败:', error);
		} finally {
		}
	}

	async function deleteFiles(ossKeys) {
		try {
			api
				.deleteFiles(selectedAppId.current, ossKeys)
				.then((_) => {
					getFiles(selectedAppId.current, getSearchParams(pagination.current, pagination.pageSize));
					message.success('Delete successfully');
				})
				.catch((error) => {
					message.error('Delete failed');
				});
		} catch (error) {
			console.error('删除失败：', error);
		}
	}

	// MARK: - app selector action handlers
	const selectedAppChanged = (appId) => {
		selectedAppId.current = appId;
		setFilter(initialFilter);
		setPagination((prev) => ({ ...prev, current: 1 }));
		onResetFilter();
	};

	// MARK: - filter action handlers
	function getSearchParams(pageIndex, pageSize) {
		const params = {
			page: pageIndex,
			size: pageSize,
			startTime: undefined,
			endTime: undefined,
			type: undefined,
			sbu: undefined,
			status: undefined,
			filename: undefined,
		};
		const dateRange = filter.dateRange;
		if (dateRange) {
			params.startTime = dateRange?.[0]?.format('YYYY-MM-DD HH:mm');
			params.endTime = dateRange?.[1]?.format('YYYY-MM-DD HH:mm');
		}
		params.type = documentTypeMapping[filter.type] ?? undefined;
		params.status = filter.status;
		params.sbu = filter.sbu;
		params.filename = filter.filename;
		return params;
	}
	const onSearch = () => {
		setPagination((prev) => ({ ...prev, current: 1 }));
		getFiles(selectedAppId.current, getSearchParams(1, pagination.pageSize));
	};

	const onResetFilter = async () => {
		setFilter(initialFilter);
		setPagination((prev) => ({ ...prev, current: 1 }));
		await getFiles(selectedAppId.current, {
			page: 1,
			size: pagination.pageSize,
		});
	};

	const showUploadModal = () => {
		if (activeKey === 'knowledgeBase') {
			setUploadModalVisible(true);
		} else if (activeKey === 'document') {
			setUploadDocumentVisible(true);
		}
	};

	// MARK: - modal actions
	const dismissUploadModal = (shouldRefresh = false) => {
		setUploadModalVisible(false);
		if (shouldRefresh) {
			onResetFilter();
		}
	};

	const cancelUploadDocument = () => {
		setUploadDocumentVisible(false);
	};

	const onUploadDocumentSuccess = () => {
		setUploadDocumentVisible(false);
		documentRef.current.fetchDocumentList(1, 10, selectedAppId.current);
	};

	// MARK: - table action handlers

	const onBatchDelete = () => {
		confirmType.current = 'batch-delete';
		setSelectedItem(null);
		setConfirmModalVisible(true);
	};

	const onDeleteButtonClicked = (id, ossKey, filename) => {
		confirmType.current = 'delete';
		setSelectedItem({ id, ossKey, filename });
		setConfirmModalVisible(true);
	};

	const onApproveButtonClicked = (ossKey, filename) => {
		confirmType.current = 'approve';
		setSelectedItem({ ossKey, filename });
		setConfirmModalVisible(true);
	};

	const onConfirm = async () => {
		let ossKeys = [];
		if (confirmType.current === 'batch-delete') {
			ossKeys = dataSource.filter((item) => selectedRowKeys.includes(item.ossKey)).map((item) => item.ossKey);
			await deleteFiles(ossKeys);
		} else if (confirmType.current === 'delete') {
			ossKeys = [selectedItem.ossKey];
			await deleteFiles(ossKeys);
		} else if (confirmType.current === 'approve') {
			try {
				await api.approveFile(selectedAppId.current, selectedItem.ossKey);
				onResetFilter();
				message.success('Approved');
			} catch (error) {
				console.error('审批失败：', error);
			}
		}
		setSelectedItem(null);
		setConfirmModalVisible(false);
	};

	async function onDownloadButtonClicked(record) {
		downloadFile(selectedAppId.current, record);
	}
	// MARK: - table data provider
	const onSelectChange = (newSelectedRowKeys) => {
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange,
		columnWidth: 40,
	};

	const columns = [
		{
			title: 'Title',
			dataIndex: 'filename',
			width: 224,
			key: 'fileName',
			ellipsis: {
				showTitle: false,
			},
			render: (text) => <div className="table-clm-title-cell">{displayText(text)}</div>,
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			width: 100,
			render: (text) => displayTextForFileExt(text),
		},
		{
			title: 'Size',
			dataIndex: 'size',
			key: 'size',
			width: 100,
			render: (text) => displayTextForFileSize(text),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 120,
			render: (text) => {
				if (!text) return '';
				const isApproved = text.toLowerCase() === 'approved';
				return (
					<span
						style={{
							backgroundColor: isApproved ? '#E3F3E0' : '#F4F4F4',
							color: isApproved ? '#3C982F' : '#696969',
							padding: '4px 8px',
							borderRadius: '4px',
							display: 'inline-block',
						}}
					>
						{text}
					</span>
				);
			},
		},
		{
			title: 'Provided by',
			dataIndex: 'sbu',
			key: 'sbu',
			width: 120,
			render: (text) => displayTextForArray(text),
		},
		{
			title: 'Update Time',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			width: 180,
			render: (text) => formatDateString(text),
		},
		{
			title: 'Uploader',
			dataIndex: 'createdBy',
			key: 'createdBy',
			width: 120,
		},
		{
			title: 'Approver',
			dataIndex: 'updatedBy',
			key: 'updatedBy',
			width: 120,
			render: (text) => displayText(text),
		},
		{
			title: 'Action',
			key: 'action',
			fixed: 'right',
			width: 100,
			render: (_, record) => {
				const pdf =
					record.type === 'pdf' ||
					(record.type && record.type.includes('pdf')) ||
					record.filename.toLowerCase().endsWith('.pdf');
				// 只有PDF文件可以预览，其他文件（包括Markdown）只能下载
				const canPreview = pdf;
				const menuItems = [
					{
						key: 'action-preview',
						icon: canPreview ? (
							<EyeOutlined style={{ fontSize: 20 }} />
						) : (
							<img
								style={{
									width: 24,
									height: 24,
								}}
								src={downloadIcon}
								alt="download"
							/>
						),
						label: canPreview ? 'View' : 'Download',
						onClick: async () => {
							await onDownloadButtonClicked(record);
						},
					},
				];

				if (
					permissions.includes(Permissions.KnowledgeBase_APPROVE) &&
					record.status &&
					record.status.toLowerCase() === 'pending'
				) {
					menuItems.push({
						key: 'action-approve',
						icon: (
							<img
								style={{
									width: 19,
									height: 19,
									marginRight: 10,
									marginLeft: 1,
								}}
								src={approveIcon}
								alt="approve"
							/>
						),
						label: 'Approve',
						onClick: () => onApproveButtonClicked(record.ossKey, record.filename),
					});
				}
				if (permissions.includes(Permissions.KnowledgeBase_DELETE)) {
					menuItems.push({
						key: 'action-delete',
						icon: <img src={deleteAction} alt="delete" style={{ marginRight: 9, marginLeft: 0 }} />,
						label: 'Delete',
						onClick: () => onDeleteButtonClicked(record.id, record.ossKey, record.filename),
					});
				}

				if (menuItems.length > 1) {
					return (
						<div className="action-drop" style={{ textAlign: 'center' }}>
							<Dropdown
								defaultOpen={true}
								placement="left"
								trigger={['click']}
								style={{ marginLeft: '-80px' }}
								menu={{
									items: menuItems,
								}}
							>
								<div style={{ cursor: 'pointer' }}>
									<img alt="more" src={moreIcon} />
								</div>
							</Dropdown>
						</div>
					);
				} else {
					return menuItems[0];
				}
			},
		},
	];

	// MARK: - render

	function _modalTitle() {
		if (confirmType.current === 'batch-delete') {
			return 'Confirm to Batch Delete';
		} else if (confirmType.current === 'delete') {
			return 'Confirm to Delete';
		} else if (confirmType.current === 'approve') {
			return 'Confirm to Approve';
		} else {
			return 'Confirm';
		}
	}

	function _modalContent() {
		if (confirmType.current === 'batch-delete') {
			return (
				<>
					<div>The selected files will be removed from the knowledge base</div>
					<div>and answer accuracy may be reduced. Are you sure to delete these files?</div>
				</>
			);
		} else if (confirmType.current === 'delete') {
			return (
				<>
					<div>The following file will be removed from the knowledge base</div>
					<div>{selectedItem?.filename}</div>
				</>
			);
		} else if (confirmType.current === 'approve') {
			return (
				<>
					<div>The following file will be imported into the knowledge base</div>
					<div>{selectedItem?.filename}</div>
				</>
			);
		} else {
			return '';
		}
	}

	return (
		<div className="app-page-container">
			<div className="app-navbar">
				<div className="app-navbar-text">Text Base Management</div>
				<div className="header-right">
					<div className="application-drop">
						<Select value={selectedAppId.current} onChange={selectedAppChanged}>
							{applicationOptions.map((item) => (
								<Option key={item.key} value={item.key}>
									{item.label}
								</Option>
							))}
						</Select>
					</div>
				</div>
			</div>
			<div className="app-tabs">
				<Tabs defaultActiveKey="knowledgeBase" items={tabsItems} onChange={(key) => setActiveKey(key)} />
				{uploadPermission && (
					<div className="app-btn app-btn-primary" onClick={showUploadModal}>
						<img src={addIcon} className="app-btn-icon" />
						Upload New
					</div>
				)}
			</div>
			<div
				style={{
					display: activeKey === 'knowledgeBase' ? 'flex' : 'none',
					flexDirection: 'column',
					height: '100%',
					flex: '1 1',
				}}
			>
				<div className="app-filter-section">
					<div className="filter-items-row">
						<div className="filter-item">
							<Input
								placeholder="Document Title"
								value={filter.filename}
								onChange={(evt) => {
									setFilter({ ...filter, filename: evt.target.value });
								}}
								className="text-input"
								allowClear
							/>
						</div>
						<Select
							placeholder="Document Type"
							value={filter.type}
							onChange={(val) => setFilter({ ...filter, type: val })}
							className="filter-item"
							allowClear
						>
							{documentTypeOptions.map((option) => (
								<Option key={option.value} value={option.value}>
									{option.label}
								</Option>
							))}
						</Select>
						<Select
							placeholder="Approval Status"
							value={filter.status}
							onChange={(val) => setFilter({ ...filter, status: val })}
							className="filter-item"
							allowClear
						>
							{approvalStatusOptions.map((option) => (
								<Option key={option.value} value={option.value}>
									{option.label}
								</Option>
							))}
						</Select>
					</div>
					<div className="filter-items-row">
						<Select
							placeholder="Provided by"
							value={filter.sbu}
							onChange={(val) => setFilter({ ...filter, sbu: val })}
							className="filter-item"
							allowClear
						>
							{providedByOptions.map((option) => (
								<Option key={option.value} value={option.value}>
									{option.label}
								</Option>
							))}
						</Select>
						<div className="filter-item">
							<RangePicker
								showTime={{ format: 'HH:mm' }}
								format="YYYY-MM-DD HH:mm"
								value={filter.dateRange}
								onChange={(val) => setFilter({ ...filter, dateRange: val })}
								placeholder={['Update Start Time', 'Update End Time']}
							/>
						</div>
						<div className="filter-actions">
							<Button color="purple" variant="filled" onClick={onSearch}>
								Search
							</Button>
							<Button variant="outlined" onClick={async () => await onResetFilter()}>
								Reset
							</Button>
						</div>
					</div>
				</div>
				{selectedRowKeys.length > 0 && permissions.includes(Permissions.KnowledgeBase_DELETE) && (
					<div className="batch-delete-header">
						<span className="desc">Please select more items to</span>
						<div className="btn" onClick={onBatchDelete}>
							<img src={deleteBatch} alt="batch delete" />
							<span>Batch Delete</span>
						</div>
					</div>
				)}
				<div className="app-table">
					<div className="app-table-content" ref={ref}>
						<Table
							rowKey="ossKey"
							size="small"
							dataSource={dataSource}
							columns={columns}
							rowSelection={permissions.includes(Permissions.KnowledgeBase_DELETE) ? rowSelection : undefined}
							pagination={false}
							scroll={{ y: tableHeight }}
						/>
					</div>
					<div className="table-line-all">
						<PaginationComponent
							pagination={pagination}
							onChange={(current, pageSize) => {
								setPagination({ ...pagination, current, pageSize });
								getFiles(selectedAppId.current, {
									page: current,
									size: pageSize,
								});
							}}
						/>
					</div>
				</div>
				<UploadModal visible={uploadModalVisible} selectedAppId={selectedAppId.current} onCancel={dismissUploadModal} />
				<ConfirmModal
					visible={confirmModalVisible}
					onCancel={() => setConfirmModalVisible(false)}
					onConfirm={onConfirm}
					title={_modalTitle()}
					contentElement={_modalContent()}
				/>
			</div>
			<div
				style={{
					display: activeKey === 'document' ? 'flex' : 'none',
					flexDirection: 'column',
					height: '100%',
					flex: '1 1',
				}}
			>
				<Documents appId={selectedAppId.current} ref={documentRef} />
				<UploadModal
					visible={uploadDocumentVisible}
					selectedAppId={selectedAppId.current}
					onCancel={cancelUploadDocument}
					isDocumentUpload={true}
					onUploadSuccess={onUploadDocumentSuccess}
				/>
			</div>
		</div>
	);
};

export default TextBase;
